import { Title, Box, Stack } from '@mantine/core';
import ListCard from './ListCard';
import ReportContext from 'context/ReportContext';
import { useContext } from 'react';
import { logger, ErrorType } from '../../../utils/logger';

const Summary = () => {
  const {
    data: { callDetails, isLoadingCallDetails },
  } = useContext(ReportContext);
  const { summary } = callDetails;
  const defaultActionItems = ['No further action'];
  const defaultDiscussionPoints = ['No discussion'];

  /**
   * 🔧 TECHNICAL DEBT: Legacy Backend Format Compatibility Handler
   * 
   * This function exists to handle inconsistent backend response formats during API transition.
   * 
   * CURRENT STATE:
   * - New backend: Returns summary fields as arrays (action: string[], discussion: string[])
   * - Legacy backend: Returns summary fields as strings or undefined
   * - Production systems may have mixed format responses
   * 
   * KNOWN ISSUES:
   * - Runtime type checking with console.error for debugging
   * - No TypeScript safety for dynamic field types
   * - Adds unnecessary complexity to component logic
   * 
   * REFACTORING NEEDED:
   * 1. Standardize backend API to always return arrays
   * 2. Add proper TypeScript union types for transition period
   * 3. Remove runtime type checking once migration complete
   * 4. Add proper error boundaries for malformed responses
   * 
   * @param field - Summary field (may be array, string, or undefined due to backend inconsistency)
   * @param defaultValue - Fallback array to use for empty/invalid data
   * @returns Normalized array format for consistent UI rendering
   */
  // Helper function to handle summary fields - backend now returns arrays directly
  const getSummaryField = (field: string[] | string | undefined, defaultValue: string[]): string[] => {
    // If field is already an array (new backend format)
    if (Array.isArray(field)) {
      return field.length > 0 ? field : defaultValue;
    }
    
    // If field is an empty string or undefined (old/legacy format), use default
    if (!field || field === '') {
      return defaultValue;
    }
    
    // If field is some other unexpected format, log error and use default
            logger.error('Unexpected summary field format', undefined, {
          errorType: ErrorType.VALIDATION_ERROR,
          component: 'getSummaryField',
          metadata: { fieldType: typeof field, hasField: !!field }
        });
    return defaultValue;
  };

  const actionItems = getSummaryField(summary?.action, defaultActionItems);
  const discussionPoints = getSummaryField(summary?.discussion, defaultDiscussionPoints);

  return (
    <Box w="100%">
      <Title size="md" mb="lg" fw={600}>
        Summary
      </Title>

      <Stack gap="md">
        <ListCard
          title="Key discussion points:"
          list={discussionPoints}
          isLoading={isLoadingCallDetails}
        />
        <ListCard
          title="Action items:"
          list={actionItems}
          isLoading={isLoadingCallDetails}
        />
      </Stack>
    </Box>
  );
};

export default Summary;
